import Big from 'big.js'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { extraAmountOf, lineTotalOf } from '../lineTotal'
import type { PfStoreState } from '../store'

/** Sum of every priced line's total plus the order-level instruction extras. Items without a
 *  snapshot contribute zero. Caller formats the `Big` for display. */
export const selectCartTotal = (state: PfStoreState): Big => {
  let total = new Big(0)
  for (const item of state.items) {
    if (!item.priceSnapshot) continue
    total = total.plus(lineTotalOf(item.priceSnapshot, item.quantity))
  }
  for (const extra of state.extrasFromInstructions) {
    total = total.plus(extraAmountOf(extra))
  }
  return total
}

export const selectProperty = (state: PfStoreState) => state.property

/** The part of a catalogue that says which products it offers. */
type CatalogueLines = { shellProducts: ReadonlyArray<{ id: string; variants?: ReadonlyArray<{ id: string }> }> }

/**
 * Cart lines whose product the catalogue no longer offers. The order and the payment cannot be built
 * for them, so a cart holding one must not be placed: it would go out with fewer lines than shown.
 * Empty while the catalogue is still loading.
 */
export const selectUnresolvedCartItemIds = (catalogue: CatalogueLines | undefined) =>
  (state: PfStoreState): CartItemId[] => {
    if (!catalogue) return []
    return state.items
      .filter((item) => {
        const shell = catalogue.shellProducts.find((sp) => sp.id === item.shellProductId)
        return !shell || (!!shell.variants && !shell.variants.some((v) => v.id === item.variantId))
      })
      .map((item) => item.id)
  }

export const selectHasUnresolvedCartItems = (catalogue: CatalogueLines | undefined) =>
  (state: PfStoreState): boolean => selectUnresolvedCartItemIds(catalogue)(state).length > 0
